import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { EngineerHead } from './Engineer';
import { NormIllustration } from './NormIllustrations';
import type { NormEntry } from './catalog';

interface Props {
  norm: NormEntry;
  onOpen: (n: NormEntry) => void;
}

export function NormCard({ norm, onOpen }: Props) {
  return (
    <article
      className="group flex h-full flex-col overflow-hidden rounded-[var(--radius-md)] border transition-colors duration-200 hover:border-[var(--accent)]"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      {/* Illustration band */}
      <div
        className="relative h-36 shrink-0 overflow-hidden"
        style={{ background: 'var(--surface-canvas)', borderBottom: '1px solid var(--border-subtle)' }}
      >
        <NormIllustration kind={norm.illustration} />
        <div
          className="absolute left-3 top-3 rounded-[var(--radius-sm)] border px-2 py-1 text-[10px] font-mono tabular tracking-wider"
          style={{
            background: 'var(--surface-1)',
            borderColor: 'var(--border-default)',
            color: 'var(--accent)',
          }}
        >
          {norm.code}
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div>
          <h3
            className="text-base"
            style={{ fontFamily: 'var(--font-display)', fontWeight: 700, letterSpacing: '-0.01em' }}
          >
            {norm.title}
          </h3>
          <p className="mt-1 text-xs" style={{ color: 'var(--text-muted)' }}>
            {norm.scope}
          </p>
        </div>

        <p className="text-sm" style={{ color: 'var(--text-secondary)', lineHeight: 1.55 }}>
          {norm.description}
        </p>

        {/* Engineer speech bubble */}
        <SpeechBubble text={norm.engineerSays} />

        {/* Highlights */}
        {norm.highlights.length > 0 && (
          <div className="mt-1 grid grid-cols-2 gap-1.5">
            {norm.highlights.slice(0, 4).map((h) => (
              <div
                key={h.label}
                className="rounded-[var(--radius-sm)] border px-2 py-1.5"
                style={{ background: 'var(--surface-2)', borderColor: 'var(--border-default)' }}
              >
                <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
                  {h.label}
                </div>
                <div className="font-mono tabular text-[11px]" style={{ color: 'var(--text-primary)' }}>
                  {h.value}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Applied in */}
        {norm.appliedIn.length > 0 && (
          <div className="mt-1">
            <div className="mb-1.5 text-[10px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>
              Aplicado en
            </div>
            <div className="flex flex-wrap gap-1.5">
              {norm.appliedIn.slice(0, 3).map((a) => (
                <span
                  key={a.module}
                  className="rounded-[var(--radius-pill)] border px-2 py-0.5 text-[10px]"
                  style={{
                    background: 'rgba(245,158,11,0.08)',
                    borderColor: 'rgba(245,158,11,0.30)',
                    color: 'var(--accent-soft)',
                  }}
                  title={a.what}
                >
                  {a.module}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <button
          onClick={() => onOpen(norm)}
          className={cn(
            'mt-auto flex items-center justify-between rounded-[var(--radius-sm)] border px-3 py-2 text-xs',
            'transition-colors duration-150',
          )}
          style={{
            borderColor: 'var(--border-default)',
            color: 'var(--text-secondary)',
            background: 'var(--surface-2)',
          }}
        >
          <span>Ver detalle de la norma</span>
          <ArrowRight size={12} strokeWidth={2} />
        </button>
      </div>
    </article>
  );
}

function SpeechBubble({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="shrink-0">
        <EngineerHead size={36} />
      </div>
      <div
        className="relative flex-1 rounded-[var(--radius-sm)] border px-3 py-2 text-[12px]"
        style={{
          background: 'var(--surface-2)',
          borderColor: 'var(--border-default)',
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}
      >
        {/* tail */}
        <span
          aria-hidden
          className="absolute -left-1.5 top-3 h-3 w-3 rotate-45 border-b border-l"
          style={{
            background: 'var(--surface-2)',
            borderColor: 'var(--border-default)',
          }}
        />
        {text}
      </div>
    </div>
  );
}
